// src/hooks/useTargetDateFilter.js
//
// The "due today" / "overdue" counts a screen shows, and the filter its chips
// toggle.
//
// Four screens want exactly this and none of them wants to own it: the counts
// have to be taken from the same rows the grid is about to render, the filter
// has to be the same three-way toggle, and pressing a chip that is already on
// has to clear it rather than re-apply it. Written once here, the four screens
// stay in step by construction.
//
// It deliberately does NOT talk to the grid's own column filters. Those are
// the user's; this is a shortcut on top of them, so the two compose — filter
// to one client, then press Overdue, and you get that client's late work.
import { useMemo, useState } from "react";

import { targetDateState } from "../utils/common";

/**
 * @param rows      the list the screen is about to show
 * @param getters   { date, status } — how to read those two off a row.
 *                  Defaults suit every list that came from vw_Queries.
 * @returns { rows, counts, mode, toggle, isFiltered }
 */
export const useTargetDateFilter = (
  rows,
  { date = (r) => r.TargetDate, status = (r) => r.Status } = {},
) => {
  /* "" = show everything. Not a boolean pair: "due" and "overdue" are mutually
     exclusive readings of the same column, and two independent flags would let
     somebody ask for neither and get an empty screen with no way to tell why. */
  const [mode, setMode] = useState("");

  const list = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  /* Counted over EVERY row the screen holds, not over the filtered result —
     otherwise pressing Overdue would make the "due today" count drop to zero
     and the chips would appear to be lying about the backlog. */
  const counts = useMemo(() => {
    let due = 0;
    let overdue = 0;

    list.forEach((r) => {
      const state = targetDateState(date(r), status(r));
      if (state === "due") due += 1;
      else if (state === "overdue") overdue += 1;
    });

    return { due, overdue };
  }, [list, date, status]);

  const filtered = useMemo(() => {
    if (!mode) return list;
    return list.filter((r) => targetDateState(date(r), status(r)) === mode);
  }, [list, mode, date, status]);

  /* Pressing the chip that is already on turns it off. A filter you can switch
     on and not off is a trap, and this one hides most of the screen. */
  const toggle = (next) => setMode((prev) => (prev === next ? "" : next));

  return { rows: filtered, counts, mode, toggle, isFiltered: !!mode };
};

export default useTargetDateFilter;
